import React, { useState, useEffect, useCallback } from 'react';
import { analyticsService, Session, Interaction } from '../services/analytics';
import './AdminDashboard.css';

export const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState(7);
  const [activeTab, setActiveTab] = useState('overview');

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboard, recentSessions] = await Promise.all([
        analyticsService.getDashboardData(timeRange),
        analyticsService.getRecentSessions(50)
      ]);
      setDashboardData(dashboard);
      setSessions(recentSessions);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Apply dark theme to body when component mounts
  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.background = '#2b2b2b';
    
    return () => {
      // Reset body styles when component unmounts
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.background = '';
    };
  }, []);



  const loadSessionInteractions = async (session: Session) => {
    try {
      setSelectedSession(session);
      const sessionInteractions = await analyticsService.getSessionInteractions(session.session_id);
      setInteractions(sessionInteractions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session data');
    }
  };

  const formatCurrency = (amount: number) => `$${amount.toFixed(4)}`;
  const formatTime = (ms: number) => `${(ms / 1000).toFixed(2)}s`;
  const formatNumber = (num: number) => num.toLocaleString();

  if (loading) {
    return <div className="admin-dashboard loading">Loading dashboard...</div>;
  }

  if (error) {
    return (
      <div className="admin-dashboard error">
        <p>Error: {error}</p>
        <button onClick={loadDashboardData}>Retry</button>
      </div>
    );
  }

  const overview = dashboardData?.overview || {};
  const usagePatterns = dashboardData?.usage_patterns || {};

  const costAnalysis = dashboardData?.cost_analysis || {};

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <h1>Sanad AI - Admin Dashboard</h1>
        <button onClick={loadDashboardData} className="refresh-btn">Refresh</button>
      </header>

      <nav className="dashboard-tabs">
        <div className="tabs-left">
          <button 
            className={activeTab === 'overview' ? 'active' : ''} 
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={activeTab === 'usage' ? 'active' : ''} 
            onClick={() => setActiveTab('usage')}
          >
            Usage Patterns
          </button>
          <button 
            className={activeTab === 'costs' ? 'active' : ''} 
            onClick={() => setActiveTab('costs')}
          >
            Cost Analysis
          </button>
          <button 
            className={activeTab === 'sessions' ? 'active' : ''} 
            onClick={() => setActiveTab('sessions')}
          >
            Chat Sessions
          </button>
        </div>
        <div className="tabs-right">
          <select value={timeRange} onChange={(e) => setTimeRange(Number(e.target.value))} className="time-range-select">
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>
      </nav>

      {activeTab === 'overview' && (
        <div className="tab-content">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Interactions</h3>
              <p className="stat-value">{formatNumber(overview.total_interactions || 0)}</p>
            </div>
            <div className="stat-card">
              <h3>Total Cost</h3>
              <p className="stat-value">{formatCurrency(overview.total_cost || 0)}</p>
            </div>
            <div className="stat-card">
              <h3>Avg Response Time</h3>
              <p className="stat-value">{formatTime(overview.avg_response_time || 0)}</p>
            </div>
            <div className="stat-card">
              <h3>Total Tokens</h3>
              <p className="stat-value">{formatNumber(overview.total_tokens || 0)}</p>
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-card">
              <h3>Daily Activity</h3>
              <div className="daily-stats">
                {overview.daily_stats?.map((day: any) => (
                  <div key={day.date} className="daily-stat">
                    <span className="date">{day.date}</span>
                    <span className="interactions">{day.total_interactions} interactions</span>
                    <span className="cost">{formatCurrency(day.total_cost)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'usage' && (
        <div className="tab-content">
          <div className="usage-grid">
            <div className="usage-card">
              <h3>Peak Usage Times</h3>
              <div className="peak-usage">
                {usagePatterns.peak_usage?.hourly_distribution && 
                  Object.entries(usagePatterns.peak_usage.hourly_distribution).map(([hour, count]) => (
                    <div key={hour} className="hour-stat">
                      <span>{`${hour}:00 - ${count} interactions`}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      )}



      {activeTab === 'costs' && (
        <div className="tab-content">
          <div className="cost-grid">
            <div className="cost-card">
              <h3>Daily Costs</h3>
              <div className="daily-costs">
                {costAnalysis.daily_costs?.map((day: any) => (
                  <div key={day.date} className="cost-day">
                    <span className="date">{day.date}</span>
                    <span className="cost">{formatCurrency(day.cost)}</span>
                    <span className="interactions">{day.interactions} interactions</span>
                    <span className="cost-per">{formatCurrency(day.cost_per_interaction)}/interaction</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="cost-card">
              <h3>Cost Summary</h3>
              <div className="cost-summary">
                <p>Total Cost: {formatCurrency(costAnalysis.total_cost || 0)}</p>
                <p>Avg Cost/Interaction: {formatCurrency(costAnalysis.avg_cost_per_interaction || 0)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="tab-content">
          <div className="sessions-grid">
            <div className="sessions-panel">
              <h3>Recent Chat Sessions</h3>
              <div className="sessions-list">
                {sessions.map(session => (
                  <div 
                    key={session.session_id} 
                    className={`session-item ${selectedSession?.session_id === session.session_id ? 'active' : ''}`}
                    onClick={() => loadSessionInteractions(session)}
                  >
                    <div className="session-info">
                      <span className="session-id">{session.session_id.substring(0, 12)}...</span>
                      <span className="session-time">
                        {new Date(session.start_time * 1000).toLocaleString()}
                      </span>
                    </div>
                    <span className="session-count">{session.message_count} messages</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="interactions-panel">
              <h3>Chat History</h3>
              {selectedSession ? (
                <div className="interactions-list">
                  {interactions.map(interaction => (
                    <div key={interaction.interaction_id} className="interaction-item">
                      <div className="interaction-user">
                        <div className="message-header">
                          <span className="sender">User</span>
                          <span className="timestamp">
                            {new Date(interaction.timestamp * 1000).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="message-text">{interaction.user_query}</div>
                      </div>
                      <div className="interaction-bot">
                        <div className="message-header">
                          <span className="sender">Bot</span>
                          <span className="response-time">{formatTime(interaction.response_time)}</span>
                          <span className="cost">{formatCurrency(interaction.estimated_cost)}</span>
                        </div>
                        <div className="message-text">{interaction.bot_response}</div>
                        <div className="interaction-meta">
                          <span>Tokens: {interaction.total_tokens}</span>
                          <span>Length: {interaction.response_length} chars</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">Select a session to view interactions</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
